import { getUnsafeChainIdToDomain, SparkDomain } from '@sparkdotfi/common-universal'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { Address } from 'viem'
import { useChainId } from 'wagmi'

import { getDefaultSavingsTokenSymbol } from './useSelectedAccount'
import { SUPPORTED_CHAIN_IDS } from '@/config/chain/constants'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'

export interface UseSyncUrlParams {
  pageChainId: number
}

export function useSyncUrl({ pageChainId }: UseSyncUrlParams): void {
  const wagmiChainId = useChainId()
  const rememberedChainId = useRef<number>(wagmiChainId)
  const { domain, savingsToken } = useParams({ strict: false })
  const { account } = useConnectedAddress({ throwOnNotConnected: false })
  const rememberedAccount = useRef<Address | undefined>(account)
  const navigate = useNavigate()

  useEffect(() => {
    // here we determine if user manually switched chain
    const newChainId = wagmiChainId
    const oldChainId = rememberedChainId.current
    rememberedChainId.current = wagmiChainId
    const oldAccount = rememberedAccount.current
    rememberedAccount.current = account

    const newChainIdIsSandbox = !SUPPORTED_CHAIN_IDS.includes(newChainId)
    const oldChainIdIsSandbox = !SUPPORTED_CHAIN_IDS.includes(oldChainId)

    if (!domain || !savingsToken) {
      // URL wasn't set to specific vault, do nothing
      return
    }

    if (newChainId === oldChainId || (newChainId === pageChainId && !newChainIdIsSandbox)) {
      return
    }

    if (oldAccount !== account && !oldChainIdIsSandbox && !newChainIdIsSandbox) {
      // account changed but it's not sandbox related - do nothing, this is either account in wallet change or connecting wallet
      return
    }

    const newDomain: SparkDomain = newChainIdIsSandbox ? 'mainnet' : getUnsafeChainIdToDomain(newChainId)

    const defaultSavingsTokenSymbol = getDefaultSavingsTokenSymbol(newChainId)
    void navigate({
      to: '/savings/$domain/$savingsToken',
      params: { domain: newDomain, savingsToken: defaultSavingsTokenSymbol.toLowerCase() },
    })
  }, [account, wagmiChainId, navigate, pageChainId, domain, savingsToken])
}
